/**
 * Subscribe to a CSS media query from React.
 *
 * The shell needs this because whether the sidebar is a drawer is a *behavioural*
 * decision, not only a styling one: a closed drawer must leave the tab order and the
 * accessibility tree (`inert`), and only the drawer has a menu button. There is one
 * `<nav>` landmark at every width (LV §123, §193).
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: it reads the current
 * match during render instead of after it, so the first paint already has the right
 * shell and there is no frame in which the wrong navigation is mounted.
 */

import { useCallback, useSyncExternalStore } from "react";

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = supported() ? window.matchMedia(query) : null;
      // Older Safari has no `addEventListener` on MediaQueryList. The delivery targets
      // evergreen browsers, but the guard costs one line and turns a hard crash on an
      // unexpected engine into a static answer.
      if (list && typeof list.addEventListener === "function") {
        list.addEventListener("change", onChange);
        return () => list.removeEventListener("change", onChange);
      }
      return () => undefined;
    },
    [query],
  );

  const getSnapshot = useCallback(
    () => (supported() ? window.matchMedia(query).matches : false),
    [query],
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/**
 * jsdom implements no media queries at all, so `window.matchMedia` is simply absent
 * under the unit suite — without this guard every test that mounts the shell dies in
 * `useSyncExternalStore` rather than in anything it meant to assert.
 *
 * A missing implementation answers "no": the query does not match, so the shell renders
 * its wide form, with the sidebar on screen. A test that needs the drawer stubs
 * `window.matchMedia` itself.
 */
function supported(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

/**
 * The one breakpoint the shell switches on in script, kept here so the value cannot
 * drift from `base.css`.
 *
 * 640px, the prototype V006's own: at and below it the sidebar is an off-canvas drawer
 * behind a menu button, which needs state; above it the sidebar is on screen — the full
 * 280px column from 1101px, the 88px icon rail between — and that difference is CSS
 * alone (#456). The top navigation's 1000px breakpoint went with it: it existed because
 * the header's min-content was 970px with five sections in it, and the header no longer
 * carries the navigation.
 */
export const NARROW_QUERY = "(max-width: 640px)";
